import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CarFront, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { FUNNEL_STEPS, STEP_IDS, getActiveStepIds, getStep } from '@/lib/funnel';
import { CAR_YEARS, CAR_MAKES, getModels, makeLogo } from '@/lib/carData';
import { useFunnel } from '@/context/FunnelContext';
import { getTracking, getSessionId, getFbc, getFbp, makeFbEventId, trackMeta } from '@/lib/tracking';
import { savePartialLead, beaconPartialLead } from '@/lib/partialLead';
import { NotchedField } from '@/components/NotchedField';
import { Button } from '@/components/ui/button';
import { tr } from '@/lib/i18n';

const RED_BTN =
  'h-14 w-full rounded-xl bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold text-lg shadow-sm transition-colors disabled:opacity-70';

/* ---------------- Year picker ---------------- */
function YearStep({ onSelect }) {
  return (
    <div
      className="mx-auto max-w-2xl grid grid-cols-3 sm:grid-cols-4 gap-[clamp(8px,1.4vh,12px)] max-h-[52vh] overflow-y-auto px-1 pb-2"
      data-testid="year-grid"
    >
      {CAR_YEARS.map((y) => (
        <button
          key={y}
          type="button"
          onClick={() => onSelect(y)}
          className="h-14 rounded-xl border border-slate-200 bg-white font-semibold text-slate-900 shadow-[0_6px_16px_rgba(15,23,42,0.05)] hover:border-[#FACC15] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(15,23,42,0.10)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300"
          data-testid={`year-option-${y}`}
        >
          {y}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Make picker (logo cards) ---------------- */
function MakeStep({ onSelect }) {
  return (
    <div
      className="mx-auto max-w-3xl grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-[clamp(8px,1.4vh,14px)] max-h-[54vh] overflow-y-auto px-1 pb-2"
      data-testid="make-grid"
    >
      {CAR_MAKES.map((m) => (
        <button
          key={m.slug || m.name}
          type="button"
          onClick={() => onSelect(m.name)}
          className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_6px_16px_rgba(15,23,42,0.05)] hover:border-[#FACC15] hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(15,23,42,0.12)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300"
          data-testid={`make-option-${m.slug || 'other'}`}
        >
          {m.slug ? (
            <img
              src={makeLogo(m.slug)}
              alt={`${m.name} logo`}
              loading="lazy"
              className="h-10 w-10 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="h-10 w-10 flex items-center justify-center rounded-full bg-yellow-50 border border-yellow-200">
              <CarFront className="h-5 w-5 text-yellow-600" />
            </span>
          )}
          <span className="text-sm font-semibold text-slate-800 text-center leading-tight">{m.name}</span>
        </button>
      ))}
    </div>
  );
}

/* ---------------- Model picker ---------------- */
function ModelStep({ make, onSelect, onChangeMake, t }) {
  const models = getModels(make);
  return (
    <>
      {make && (
        <div className="mx-auto max-w-2xl mb-4 flex items-center justify-center gap-2 text-sm" data-testid="model-current-make">
          <span className="text-slate-500">{t.fields?.makeLabel || 'Make'}:</span>
          <span className="font-semibold text-slate-900">{make}</span>
          <button
            type="button"
            onClick={onChangeMake}
            className="ml-1 font-semibold text-[#EF4444] hover:underline"
            data-testid="model-change-make-button"
          >
            {t.buttons?.changeMake || 'Change'}
          </button>
        </div>
      )}
      <div
        className="mx-auto max-w-2xl grid grid-cols-2 sm:grid-cols-3 gap-[clamp(8px,1.4vh,12px)] max-h-[52vh] overflow-y-auto px-1 pb-2"
        data-testid="model-grid"
      >
      {models.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onSelect(m)}
          className="min-h-14 px-3 py-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-900 shadow-[0_6px_16px_rgba(15,23,42,0.05)] hover:border-[#FACC15] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(15,23,42,0.10)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300"
          data-testid={`model-option-${m.replace(/[^a-zA-Z0-9]/g, '-')}`}
        >
          {m}
        </button>
      ))}
      </div>
    </>
  );
}

/* ---------------- Name step ---------------- */
function NameStep({ answers, setAnswer, onNext, t }) {
  const [first, setFirst] = useState(answers.first_name || '');
  const [last, setLast] = useState(answers.last_name || '');
  const [errors, setErrors] = useState({});

  // A "real" name must be >=2 chars AND contain at least 2 letters, and not
  // be one of the common placeholders visitors sometimes submit ("K", "NA",
  // "test", "asdf" etc.). Same guardrail the backend now enforces so the
  // rejection happens client-side without an API round-trip.
  const isValidName = (v) => {
    const s = (v || '').trim();
    if (s.length < 2) return false;
    const letters = (s.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) || []).length;
    if (letters < 2) return false;
    if (['NA', 'N/A', 'NONE', 'TEST', 'TESTING', 'ASDF', 'XX'].includes(s.toUpperCase())) return false;
    return true;
  };

  const submit = (e) => {
    e.preventDefault();
    const er = {};
    if (!isValidName(first)) er.first = t.errors.first;
    if (!isValidName(last)) er.last = t.errors.last;
    setErrors(er);
    if (Object.keys(er).length) return;
    setAnswer('first_name', first.trim());
    setAnswer('last_name', last.trim());
    onNext();
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full grid gap-4">
      <NotchedField label={t.fields.firstName} value={first} onChange={(e) => setFirst(e.target.value)}
        placeholder={t.fields.firstPh} error={errors.first} data-testid="name-first-input" autoFocus />
      <NotchedField label={t.fields.lastName} value={last} onChange={(e) => setLast(e.target.value)}
        placeholder={t.fields.lastPh} error={errors.last} data-testid="name-last-input" />
      <Button type="submit" className={`mt-3 ${RED_BTN}`} data-testid="name-continue-button">{t.buttons.continue}</Button>
    </form>
  );
}

/* ---------------- Phone step ---------------- */
function PhoneStep({ answers, setAnswer, onNext, t }) {
  const [phone, setPhone] = useState(answers.phone || '');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[0-9+()\-\s]{7,}$/.test(phone.trim())) {
      setError(t.errors.phone);
      return;
    }
    setError('');
    setChecking(true);
    try {
      const res = await api.post('/verify-phone', { phone: phone.trim(), region: 'US' });
      if (res.data.valid) {
        const clean = res.data.formatted || phone.trim();
        setAnswer('phone', clean);
        // Partial-save: visitor just gave us a verified phone. Captures
        // the lead into Admin → Abandoned Leads if they bail before the
        // email step; auto-dismissed when the full /leads submit fires.
        savePartialLead({ step_reached: 'phone', phone: clean },
                        { ...answers, phone: clean });
        onNext();
      } else {
        setError(t.errors.phoneReal);
      }
    } catch (err) {
      setAnswer('phone', phone.trim());
      savePartialLead({ step_reached: 'phone', phone: phone.trim() },
                      { ...answers, phone: phone.trim() });
      onNext();
    } finally {
      setChecking(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full">
      <NotchedField label={t.fields.phone} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
        placeholder={t.fields.phonePh} error={error} data-testid="phone-input" autoFocus />
      <Button type="submit" disabled={checking} className={`mt-7 ${RED_BTN}`} data-testid="phone-continue-button">
        {checking ? t.buttons.checking : t.buttons.continue}
      </Button>
    </form>
  );
}

/* ---------------- Email step (final → submit) ---------------- */
function EmailStep({ answers, setAnswer, onSubmit, submitting, t }) {
  const [email, setEmail] = useState(answers.email || '');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  // Abandoned-email beacon: if the visitor types a plausible email then
  // closes the tab / backs out BEFORE clicking Submit, push whatever
  // we've got via sendBeacon so Admin → Abandoned Leads still gets the
  // row. The main /leads submit will auto-dismiss them if they come
  // back and finish.
  useEffect(() => {
    const onLeave = () => {
      const addr = (email || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) return;
      beaconPartialLead(
        { step_reached: 'email_unsubmitted', email: addr },
        { ...answers, email: addr },
      );
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [email, answers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError(t.errors.email);
      return;
    }
    setError('');
    setChecking(true);
    try {
      const res = await api.post('/verify-email', { email: email.trim() });
      if (!res.data.valid) {
        setError(res.data.reason === 'undeliverable' ? t.errors.emailUndeliverable : t.errors.email);
        setChecking(false);
        return;
      }
      const clean = res.data.normalized || email.trim();
      setEmail(clean);
      setAnswer('email', clean);
      onSubmit(clean);
    } catch (err) {
      setAnswer('email', email.trim());
      onSubmit(email.trim());
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full">
      <NotchedField label={t.fields.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t.fields.emailPh} error={error} data-testid="email-input" autoFocus />
      <Button type="submit" disabled={submitting || checking} className={`mt-7 ${RED_BTN}`} data-testid="email-submit-button">
        {submitting || checking ? t.buttons.submitting : t.buttons.submit}
      </Button>
      <p className="mt-6 text-[11px] leading-relaxed text-slate-400 text-center">
        {t.consent}{' '}
        <span className="text-blue-500">{t.consentTerms}</span> {t.consentAnd}{' '}
        <span className="text-blue-500">{t.consentPrivacy}</span>.
      </p>
    </form>
  );
}

/* ---------------- Mobile-only "Not sure? Text us" helper ----------------
   Renders under every funnel step on mobile viewports (<768px). Desktop
   hides it because most desktop browsers can't launch an SMS composer.

   The SMS number matches the landing page:
     • Spanish flow (source_page in sp/spa/laspa/dgs/ladgs/es) → 866-524-3722
     • Everything else → 844-335-8911

   Two-step UX:
     1. Collapsed pill: "Not sure? Text us" (or Spanish equivalent).
     2. Tap → expands inline to show a textarea prefilled with a friendly
        intro that includes any vehicle year/make/model the user already
        entered. The client can edit / replace the message freely.
     3. Tap "Send Text" → opens the OS-native SMS composer with THEIR
        composed body, and stamps `contact_preference="text"` +
        `message="<their text>"` onto the funnel state. If the user
        subsequently completes the funnel form, the /api/leads payload
        carries `message` through, and the server writes it into
        Quickbase Intakes Notes (field 36) prefixed with "Client message:".
        Main funnel steps are untouched — this input lives only in the
        text-us panel, on mobile, opt-in. */
function TextUsHelper({ answers, setAnswer, lang }) {
  // Canonical list of source_page codes that mean "Spanish landing page".
  // Kept in sync with backend `_SPANISH_SOURCE_PAGES` (server.py) so the
  // SMS number, prefill copy, and QB source routing all agree.
  const SPANISH_SOURCES = new Set(['sp', 'spa', 'laspa', 'dgs', 'ladgs', 'es']);
  const sp = String(answers.source_page || '').toLowerCase();
  const isSpanish = lang === 'es' || SPANISH_SOURCES.has(sp);
  const smsNumber = isSpanish ? '18665243722' : '18443358911';

  const pillLabel = isSpanish ? '¿No está seguro?' : 'Not sure?';
  const pillCta   = isSpanish ? 'Envíenos un texto' : 'Text us';
  const msgLabel  = isSpanish ? 'Su mensaje' : 'Your message';
  const sendLabel = isSpanish ? 'Enviar texto' : 'Send text';
  const cancel    = isSpanish ? 'Cancelar' : 'Cancel';

  // Default prefill body carries any vehicle info the user already typed so
  // the CM sees context on the very first inbound message. The client can
  // freely edit this before sending.
  const carBits = [answers.car_year, answers.car_make, answers.car_model].filter(Boolean).join(' ');
  const defaultBody = isSpanish
    ? (carBits ? `Hola, me interesa una revisión gratuita. Mi vehículo: ${carBits}.` : 'Hola, me interesa una revisión gratuita.')
    : (carBits ? `Hi! I'd like a free case review. My vehicle: ${carBits}.` : "Hi! I'd like a free case review.");

  const [open, setOpen] = useState(false);
  const [msg, setMsg]   = useState(answers.message || defaultBody);

  const onSend = () => {
    const trimmed = (msg || '').trim();
    try {
      // Persist so /api/leads → QB Notes (field 36) picks it up if the user
      // also submits the funnel form.
      setAnswer('contact_preference', 'text');
      setAnswer('message', trimmed);
    } catch (e) { /* noop */ }
    // Open native SMS composer with the client-composed body.
    const href = `sms:${smsNumber}?&body=${encodeURIComponent(trimmed || defaultBody)}`;
    window.location.href = href;
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden mt-4 mx-auto flex items-center justify-center gap-2 rounded-xl bg-white/80 border border-emerald-200 px-4 py-3 text-center text-sm font-semibold text-emerald-800 hover:bg-emerald-50 shadow-sm w-full max-w-md"
        data-testid="funnel-text-us-helper"
      >
        <MessageSquare className="h-4 w-4 text-emerald-600" />
        <span>
          <span className="text-slate-700">{pillLabel}</span>{' '}
          <span className="underline underline-offset-2">{pillCta}</span>
        </span>
      </button>
    );
  }

  return (
    <div
      className="md:hidden mt-4 mx-auto rounded-xl bg-white border border-emerald-200 p-4 shadow-sm w-full max-w-md"
      data-testid="funnel-text-us-panel"
    >
      <label className="block">
        <span className="text-sm font-semibold text-slate-700">{msgLabel}</span>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={3}
          maxLength={500}
          className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
          data-testid="funnel-text-us-message"
          autoFocus
        />
      </label>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          data-testid="funnel-text-us-cancel"
        >
          {cancel}
        </button>
        <button
          type="button"
          onClick={onSend}
          className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 shadow-sm"
          data-testid="funnel-text-us-send"
        >
          <MessageSquare className="h-4 w-4" />
          <span>{sendLabel}</span>
        </button>
      </div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function FunnelStep() {
  const { step: stepId } = useParams();
  const navigate = useNavigate();
  const { answers, setAnswer, lang } = useFunnel();
  const t = tr(lang);
  const [submitting, setSubmitting] = useState(false);

  const step = getStep(stepId);
  const stepIds = getActiveStepIds(answers);
  const index = stepIds.indexOf(stepId);
  const total = stepIds.length;
  const stepText = (t.steps && t.steps[stepId]) || {};

  useEffect(() => {
    if (!answers.started) {
      navigate(lang === 'es' ? '/sp' : '/', { replace: true });
      return;
    }
    if (!step) {
      navigate(`/flow/${stepIds[0]}`, { replace: true });
      return;
    }
    // Step is valid but skipped for this session (e.g. make already chosen on /pa)
    // — jump to the next active step instead of showing it again.
    if (index < 0) {
      const full = FUNNEL_STEPS.map((s) => s.id);
      const orig = full.indexOf(stepId);
      const nextActive = full.slice(orig + 1).find((id) => stepIds.includes(id)) || stepIds[stepIds.length - 1];
      navigate(`/flow/${nextActive}`, { replace: true });
      return;
    }
    window.scrollTo(0, 0);
    // Record furthest funnel step reached (for drop-off analytics).
    if (step && index >= 0) {
      api.post('/track/step', { session_id: getSessionId(), step: stepId, index }).catch(() => {});
    }
  }, [stepId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!step || index < 0) return null;

  const goNext = () => {
    if (index < total - 1) navigate(`/flow/${stepIds[index + 1]}`);
  };

  const selectAndNext = (field, value) => {
    setAnswer(field, value);
    setTimeout(goNext, 140);
  };

  // Re-open the make step when it was pre-selected on a landing page (/pa, /spa).
  const changeMake = () => {
    setAnswer('make_locked', '');
    setAnswer('car_make', '');
    setAnswer('car_model', '');
    navigate('/flow/make');
  };

  const submitLead = async (emailVal) => {
    setSubmitting(true);
    try {
      const tracking = getTracking();
      const fbEventId = makeFbEventId('lead');
      const fbc = getFbc();
      const fbp = getFbp();
      const payload = {
        car_year: answers.car_year || '',
        car_make: answers.car_make || '',
        car_model: answers.car_model || '',
        first_name: answers.first_name || '',
        last_name: answers.last_name || '',
        phone: answers.phone || '',
        email: emailVal || answers.email || '',
        session_id: getSessionId(),
        source_page: answers.source_page || 'home',
        // Optional client-typed free-text from the last funnel step. When
        // non-empty, the server writes it into QB Intakes Notes (field 36)
        // prefixed with "Client message:" so intake sees the context.
        message: (answers.message || '').trim(),
        // Contact preference — set to "text" when the user tapped the mobile
        // "Not sure? Text us" helper below any funnel step. Drives QB source
        // selection ("Landing Page - Text Message" / SP variant) on the server.
        contact_preference: answers.contact_preference || '',
        campaign_id: tracking.campaign_id,
        adgroup_id: tracking.adgroup_id,
        ad_id: tracking.ad_id,
        keyword: tracking.keyword,
        // Meta / Facebook Ads pass human-readable names via utm_* macros. Send
        // them along so Meta leads land in the CRM with real ad names, not
        // just numeric IDs (Google's `ad_labels` map doesn't cover FB).
        campaign_name: tracking.campaign_name || '',
        adgroup_name: tracking.adgroup_name || '',
        ad_name: tracking.ad_name || '',
        utm_source: tracking.utm_source || '',
        // Email-marketing attribution. CTAs in our marketing templates stamp
        // `?tpl=<KEY>&tpl_lang=<en|es>` on every funnel link. Forwarding both
        // the explicit template key AND the raw `utm_campaign` ensures the
        // backend (server.py create_lead → quickbase_service) tags the lead
        // as Source="Email Marketing" even if any one of these channels gets
        // stripped by a redirect or UTM-blocking browser extension.
        source_template:      (tracking.params && tracking.params.tpl) || '',
        source_template_lang: (tracking.params && tracking.params.tpl_lang) || '',
        utm_campaign:         (tracking.params && tracking.params.utm_campaign) || tracking.campaign_name || '',
        utm_medium:           (tracking.params && tracking.params.utm_medium) || '',
        placement:     tracking.placement || '',
        gclid: tracking.gclid,
        gbraid: tracking.gbraid,
        wbraid: tracking.wbraid,
        referrer: tracking.referrer,
        feeditemid: tracking.feeditemid,
        extensionid: tracking.extensionid,
        split_experiment_id: tracking.split_experiment_id,
        split_variant: tracking.split_variant,
        params: tracking.params,
        // Meta / Facebook Ads dedupe + attribution keys.
        fb_event_id: fbEventId,
        fbc,
        fbp,
        fbclid: (typeof window !== 'undefined' && localStorage.getItem('lp_fbclid')) || '',
      };
      // Fire the browser Meta Pixel `Lead` event first (with the same event_id
      // the server will use for CAPI), then POST to /leads. Meta dedupes the
      // two events by matching event_id — no double-counting.
      trackMeta('Lead', { content_name: 'Lemon Law case review', currency: 'USD', value: 0 }, fbEventId);
      const res = await api.post('/leads', payload);
      // Stash the created lead's id so the Thank-You page can pass it as
      // Google Ads' `transaction_id` — matches the server's `order_id` on the
      // offline conversion upload, so Enhanced Conversions dedup correctly.
      // Without this, Google's Diagnostics flags "Tag and API order IDs do not
      // match" and enhanced user-data isn't applied to the offline import.
      try {
        const newLeadId = res?.data?.id || '';
        if (newLeadId) sessionStorage.setItem('lp_last_lead_id', newLeadId);
        // Stash the raw user-provided data so ThankYou can pass it to the
        // Google Ads Enhanced Conversion tag (hashed client-side by gtag).
        // Session-scoped so it clears when the tab closes.
        const eud = {
          email: payload.email || '',
          phone: payload.phone || '',
          first_name: payload.first_name || '',
          last_name: payload.last_name || '',
        };
        sessionStorage.setItem('lp_last_lead_eud', JSON.stringify(eud));
      } catch (e) { /* sessionStorage disabled — non-fatal */ }
      navigate('/thank-you');
    } catch (e) {
      toast.error(t.errors.submit);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-full flex flex-col justify-start px-4 sm:px-6 pt-[clamp(16px,3.5vh,40px)] pb-8"
      data-testid="page-flow"
    >
      <motion.div
        key={stepId}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="w-full max-w-3xl mx-auto"
        data-testid="flow-step-container"
      >
        <div className="text-center mb-[clamp(14px,2.5vh,26px)]">
          <h2 className="font-mock font-extrabold text-[clamp(1.6rem,4vw,2.5rem)] text-[#0F1B3D] leading-tight" data-testid="flow-question">
            {stepText.q}
          </h2>
          {stepText.sub && (
            <p className="mt-2 text-[clamp(0.95rem,1.7vw,1.125rem)] font-semibold text-[#EF4444]" data-testid="flow-subtitle">{stepText.sub}</p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-[0_18px_50px_rgba(15,27,61,0.16)] p-4 sm:p-6">
          {step.type === 'year' && <YearStep onSelect={(v) => selectAndNext('car_year', v)} />}
          {step.type === 'make' && <MakeStep onSelect={(v) => selectAndNext('car_make', v)} />}
          {step.type === 'model' && <ModelStep make={answers.car_make} onSelect={(v) => selectAndNext('car_model', v)} onChangeMake={changeMake} t={t} />}
          {step.type === 'name' && <NameStep answers={answers} setAnswer={setAnswer} onNext={goNext} t={t} />}
          {step.type === 'phone' && <PhoneStep answers={answers} setAnswer={setAnswer} onNext={goNext} t={t} />}
          {step.type === 'email' && (
            <EmailStep answers={answers} setAnswer={setAnswer} onSubmit={submitLead} submitting={submitting} t={t} />
          )}
        </div>

        {/* Mobile "Text us" helper temporarily disabled per client request
            (Feb 2026). CTA + prefilled SMS composer removed — leads must now
            submit the funnel form or call. TextUsHelper component is kept in
            source for easy re-enablement later. */}
      </motion.div>
    </div>
  );
}
